<template>
  <a-drawer
      v-if="!isMobile"
      :width="600"
      placement="right"
      :closable="false"
      :open="show"
      @close="close"
  >
    <template #title>
      <div :class="{ new: hasNew }" class="drawer-title">
        <div v-if="!currentAction" class="notification-title">
          <span>Уведомления</span>
          <div class="dot">{{ latestNotifications.newCount }}</div>
        </div>

        <div v-else class="sub-header">
          <div class="back-button-wrapper action-button-wrapper" @click="clearCurrentAction">
            <LeftOutlined class="back-button" />
          </div>
          <div>
            <span>{{ headerTranslate[currentAction] }}</span>
          </div>
        </div>

        <div class="close-icon" @click="close">
          <CloseOutlined />
        </div>
      </div>
    </template>

    <div v-if="!currentAction" class="tabs">
      <a-tabs
          v-model:activeKey="menuKey"
          class="simple-green-tabs"
          :animated="false"
          @change="clearCurrentAction"
      >
        <a-tab-pane key="notifications" tab="Уведомления" />
        <a-tab-pane key="reminders" tab="Мои напоминания" />
      </a-tabs>
    </div>

    <div v-if="isPreloader" class="spinner">
      <a-spin />
    </div>

    <div v-else>
      <div v-if="!currentAction">
        <template v-if="menuKey === 'notifications'">
          <div v-if="!latestNotifications.notifications.length" class="grey-text empty-result">
            <span>Нет уведомлений</span>
          </div>

          <div class="notification-wrapper">
            <div
                v-for="noticeData in latestNotifications.notifications"
                :key="noticeData.date"
            >
              <a-divider style="margin: 12px 0" orientation="left">
                <span
                    class="date-header"
                    :class="{ 'grey-text': !noticeData.created_today, recent: noticeData.created_today }"
                >
                  {{ noticeData.created_today ? 'Сегодня' : noticeData.date }}
                </span>
              </a-divider>

              <div class="notice-card-list">
                <div
                    v-for="notice in noticeData.items"
                    :key="notice.id || notice.send_at"
                >
                  <a-card
                      class="notice-card"
                      :bordered="notice.read"
                      :class="{ 'new-notice': !notice.read }"
                  >
                    <div class="notice-card-content">
                      <div>{{ notice.message }}</div>

                      <div v-if="!notice.read">
                        <a-button
                            class="secondary-button"
                            :loading="actionLoading"
                            @click="readNotification(notice.id)"
                        >
                          Прочитать
                        </a-button>
                      </div>
                    </div>
                  </a-card>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-if="menuKey === 'reminders'">
          <div v-if="!latestNotifications.reminders.length" class="grey-text empty-result">
            <span>Нет напоминаний</span>
          </div>

          <div class="notification-wrapper">
            <div
                v-for="noticeData in latestNotifications.reminders"
                :key="noticeData.date"
            >
              <a-divider style="margin: 12px 0" orientation="left">
                <span
                    class="date-header"
                    :class="{ 'grey-text': !noticeData.created_today, recent: noticeData.created_today }"
                >
                  {{ noticeData.created_today ? 'Сегодня' : noticeData.date }}
                </span>
              </a-divider>

              <div class="notice-card-list">
                <div
                    v-for="notice in noticeData.items"
                    :key="notice.id || notice.send_at"
                >
                  <a-card class="notice-card">
                    <div class="notice-card-content">
                      <div>{{ notice.message }}</div>

                      <div v-if="!notice.sent" class="control-button-wrapper">
                        <a-button
                            class="secondary-button control-button"
                            @click="editReminder(notice)"
                        >
                          <EditOutlined />
                          <span v-if="isMobile">Редактировать</span>
                        </a-button>

                        <a-popconfirm
                            placement="topRight"
                            :icon="null"
                            ok-text="Подтвердить"
                            cancel-text="Отменить"
                            overlay-class-name="reset-template-popconfirm"
                            @confirm="deleteReminder(notice.id)"
                        >
                          <template #title>
                            <div>
                              <div class="pc-head">
                                <ExclamationCircleFilled class="pc-warn-ic" />
                                <div class="pc-text">
                                  <div class="pc-title">Подтвердите действие</div>
                                </div>
                              </div>

                              <div class="pc-desc">
                                Напоминание удалится без возможности восстановления.
                              </div>
                            </div>
                          </template>

                          <a-button
                              class="secondary-button control-button"
                              :loading="actionLoading"
                          >
                            <DeleteOutlined />
                            <span v-if="isMobile">Удалить</span>
                          </a-button>
                        </a-popconfirm>
                      </div>
                    </div>
                  </a-card>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <template v-if="currentAction === 'settings'">
        <div class="setting-form">
          <a-card class="setting-card-block">
            <div class="setting-title">
              Приглашение пройти тестирование
            </div>

            <div class="settings-column">
              <template v-if="getRight('adminNotifications')">
                <div class="setting-group">
                  <div class="grey-text setting-label">Отправить оповещение сотруднику:</div>
                  <div class="setting-block">
                    <div class="setting-row">
                      <a-switch v-model:checked="settingsForm.notifyEmployee3Days" />
                      <div>Если он не прошел тест в течение 3-х дней</div>
                    </div>

                    <div class="setting-row">
                      <a-switch v-model:checked="settingsForm.notifyEmployee7Days" />
                      <div>Если он не прошел тест в течение недели</div>
                    </div>
                  </div>
                </div>
              </template>

              <div class="setting-group">
                <div class="grey-text setting-label">Отправить оповещение мне:</div>
                <div class="setting-block">
                  <template v-if="getRight('adminNotifications')">
                    <div class="setting-row">
                      <a-switch v-model:checked="settingsForm.notifyAdmin3Days" />
                      <div>Если сотрудник не прошел тест в течение 3-х дней</div>
                    </div>

                    <div class="setting-row">
                      <a-switch v-model:checked="settingsForm.notifyAdmin7Days" />
                      <div>Если сотрудник не прошел тест в течение недели</div>
                    </div>
                  </template>

                  <div class="setting-row">
                    <a-switch v-model:checked="settingsForm.notifySelf3Days" />
                    <div>Если тест не пройден в течение 3-х дней</div>
                  </div>

                  <div class="setting-row">
                    <a-switch v-model:checked="settingsForm.notifySelf7Days" />
                    <div>Если тест не пройден в течение недели</div>
                  </div>
                </div>
              </div>
            </div>
          </a-card>

          <a-card class="setting-card-block">
            <div class="settings-card">
              <div class="setting-title">
                Приглашение в ЛК
              </div>

              <div class="settings-column">
                <div class="setting-group">
                  <template v-if="getRight('adminNotifications')">
                    <div class="grey-text setting-label">Отправить оповещение сотруднику:</div>
                    <div class="setting-block">
                      <div class="setting-row">
                        <a-switch v-model:checked="settingsForm.notifyEmployeeCourseExpires" />
                        <div>За 14 дней до окончания доступа к курсу</div>
                      </div>
                    </div>
                  </template>
                </div>

                <div class="setting-group">
                  <div class="grey-text setting-label">Отправить оповещение мне</div>
                  <div class="setting-block">
                    <div v-if="getRight('adminNotifications')" class="setting-row">
                      <a-switch v-model:checked="settingsForm.notifyAdminCourseExpires" />
                      <div>За 14 дней до окончания доступа сотрудника к курсу</div>
                    </div>

                    <div class="setting-row">
                      <a-switch v-model:checked="settingsForm.notifySelfCourseExpires" />
                      <div>За 14 дней до окончания доступа к курсу</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </a-card>

          <a-alert show-icon type="info" class="blue-alert">
            <template #message>
              <div>
                Если у вас возникли вопросы,
                <span class="feedback-action-text" @click="showFeedbackForm">свяжитесь с нами</span>.
              </div>
            </template>

            <template #icon>
              <InfoCircleFilled style="color: var(--main-blue)" />
            </template>
          </a-alert>
        </div>
      </template>

      <template v-if="isReminderAction">
        <a-form layout="vertical" @finish="handleAction" class="notice-form">
          <div style="display: flex; gap: 16px">
            <a-form-item label="Дата">
              <a-date-picker
                  v-model:value="reminderForm.date"
                  format="DD.MM.YYYY"
                  style="width: 180px"
                  :show-now="false"
                  :need-confirm="false"
              />
            </a-form-item>

            <a-form-item label="Время">
              <a-time-picker
                  v-model:value="reminderForm.time"
                  format="HH:mm"
                  style="width: 140px"
                  :show-now="false"
                  :minute-step="5"
              />
            </a-form-item>
          </div>

          <a-form-item label="Текст напоминания">
            <a-textarea
                v-model:value="reminderForm.message"
                :rows="5"
                :auto-size="{ minRows: 5 }"
            />
          </a-form-item>

          <a-form-item>
            <a-checkbox v-model:checked="reminderForm.send_to_email">
              Продублировать на мою почту
            </a-checkbox>
          </a-form-item>
        </a-form>
      </template>

      <template v-if="currentAction === 'edit_mail_templates'">
        <TemplateEditor
            ref="templateEditor"
            @dirty-change="onTemplateEditorDirtyChange"
        />
      </template>
    </div>

    <template #footer>
      <div class="footer-button" :class="{ 'mobile-footer': isMobile }">
        <template v-if="!currentAction">
          <div class="footer-button-wrapper">
            <a-dropdown :trigger="['click']" placement="bottomLeft">
              <a-button class="secondary-button" @click.prevent>
                <SettingOutlined />
              </a-button>

              <template #overlay>
                <a-menu @click="handleMenuClick">
                  <a-menu-item key="settings">Настроить уведомления</a-menu-item>
                  <a-menu-item
                      v-if="getRight('admin')"
                      key="edit_mail_templates"
                  >
                    Шаблоны почтовых писем
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>

            <a-button class="secondary-button" @click="openReminderForm">
              <template #icon>
                <PlusOutlined />
              </template>
              <span v-if="!isMobile">Создать напоминание</span>
            </a-button>
          </div>

          <div v-if="latestNotifications.newCount" class="read-all-button">
            <a-button
                class="secondary-button"
                :loading="actionLoading"
                @click="readAllNotifications"
            >
              Прочитать все
            </a-button>
          </div>
        </template>

        <template v-else>
          <div>
            <a-button class="secondary-button" @click="clearCurrentAction">
              Отменить
            </a-button>
          </div>

          <div>
            <a-button
                class="primary-button"
                :loading="actionLoading"
                :disabled="!canSave"
                @click="handleAction"
            >
              Сохранить
            </a-button>
          </div>
        </template>
      </div>
    </template>
  </a-drawer>

  <CloseNotificationModal
      :open="closeConfirmOpen"
      @cancel="cancelCloseConfirm"
      @close="confirmClose"
  />
</template>

<script>
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import {
  CloseOutlined,
  PlusOutlined,
  SettingOutlined,
  LeftOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleFilled, ReloadOutlined, ExclamationCircleFilled,
} from "@ant-design/icons-vue";
import { message } from "ant-design-vue";
import dayjs from "dayjs";

import useCheckRights from "@/mixins/useCheckRights";
import TemplateEditor from "@/components/Notification/TemplateEditor.vue";
import CloseNotificationModal from "@/components/Forms/Notifications/CloseNotificationModal.vue";

export default {
  name: "notification-bar",
  components: {
    CloseNotificationModal,
    ExclamationCircleFilled,
    ReloadOutlined,
    TemplateEditor,
    InfoCircleFilled,
    PlusOutlined,
    CloseOutlined,
    SettingOutlined,
    LeftOutlined,
    DeleteOutlined,
    EditOutlined,
  },
  emits: ["close"],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const store = useStore();
    const { getRight } = useCheckRights();

    const latestNotifications = computed(() => {
      const data = store.state.notifications.latestNotifications || {};

      return {
        notifications: data.notifications || [],
        reminders: data.reminders || [],
        newCount: data.newCount || 0,
      };
    });

    const optionsList = computed(() => store.state.notifications.optionsList || {});

    const isPreloader = ref(true);
    const menuKey = ref("notifications");
    const currentAction = ref(null);
    const actionLoading = ref(false);
    const isMobile = ref(window.innerWidth <= 768);

    const templateEditor = ref(null);
    const currentReminderToEdit = ref(null);

    const headerTranslate = {
      settings: "Настройка уведомлений",
      reminder_form: "Уведомление",
      edit_reminder: "Редактирование напоминания",
      edit_mail_templates: "Настройка шаблонов писем",
    };

    const hasNew = computed(() => latestNotifications.value.newCount > 0);
    const isReminderAction = computed(() => {
      return currentAction.value === "reminder_form" || currentAction.value === "edit_reminder";
    });

    const settingsForm = reactive({
      notifyEmployee3Days: false,
      notifyEmployee7Days: false,
      notifyAdmin3Days: false,
      notifyAdmin7Days: false,
      notifyAdminCourseExpires: false,
      notifyEmployeeCourseExpires: false,
      notifySelf3Days: false,
      notifySelf7Days: false,
      notifySelfCourseExpires: false,
    });

    const emptyReminderForm = () => ({
      message: "",
      date: null,
      time: null,
      send_to_email: false,
    });

    const reminderForm = reactive(emptyReminderForm());

    const normalizeReminderForm = (form) => ({
      message: (form.message || "").trim(),
      date: form.date ? dayjs(form.date).format("YYYY-MM-DD") : null,
      time: form.time ? dayjs(form.time).format("HH:mm") : null,
      send_to_email: !!form.send_to_email,
    });

    const settingsInitial = ref({});
    const reminderInitial = ref(normalizeReminderForm(emptyReminderForm()));
    const mailTemplatesDirty = ref(false);

    const isSettingsDirty = computed(() => {
      return JSON.stringify(settingsForm) !== JSON.stringify(settingsInitial.value);
    });

    const isReminderDirty = computed(() => {
      return (
          JSON.stringify(normalizeReminderForm(reminderForm)) !==
          JSON.stringify(reminderInitial.value)
      );
    });

    const canSave = computed(() => {
      if (currentAction.value === "settings") {
        return isSettingsDirty.value;
      }

      if (isReminderAction.value) {
        return isReminderDirty.value;
      }

      if (currentAction.value === "edit_mail_templates") {
        return mailTemplatesDirty.value;
      }

      return false;
    });

    const onResize = () => {
      isMobile.value = window.innerWidth <= 768;
    };

    const fetchLatestNotifications = async () => {
      isPreloader.value = true;

      try {
        await Promise.all([
          store.dispatch("notifications/getReminders"),
          store.dispatch("notifications/getLatest"),
        ]);
      } finally {
        isPreloader.value = false;
      }
    };

    const initializeData = async () => {
      isPreloader.value = true;

      try {
        await Promise.all([
          store.dispatch("notifications/getOptionsLabels"),
          store.dispatch("notifications/getOptionsList"),
          store.dispatch("notifications/getReminders"),
          store.dispatch("notifications/getLatest"),
        ]);

        Object.assign(settingsForm, optionsList.value);
        settingsInitial.value = { ...settingsForm };
        reminderInitial.value = normalizeReminderForm(reminderForm);
      } finally {
        isPreloader.value = false;
      }
    };

    onMounted(() => {
      window.addEventListener("resize", onResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("resize", onResize);
    });

    onBeforeMount(async () => {
      await initializeData();
    });

    watch(
        () => props.show,
        async (isOpen) => {
          if (isOpen) {
            await fetchLatestNotifications();
          }
        }
    );

    const readNotification = async (id) => {
      actionLoading.value = true;

      try {
        await store.dispatch("notifications/readNotification", { id });
        await fetchLatestNotifications();
      } finally {
        actionLoading.value = false;
      }
    };

    const readAllNotifications = async () => {
      actionLoading.value = true;

      try {
        await store.dispatch("notifications/readAllNotifications");
        await fetchLatestNotifications();
      } finally {
        actionLoading.value = false;
      }
    };

    const closeConfirmOpen = ref(false);

    const emitClose = () => {
      context.emit("close");
    };

    const cancelCloseConfirm = () => {
      closeConfirmOpen.value = false;
    };

    const confirmClose = () => {
      closeConfirmOpen.value = false;
      clearCurrentAction();
      emitClose();
    };

    const close = () => {
      if (hasUnsavedChanges.value) {
        closeConfirmOpen.value = true;
        return;
      }

      emitClose();
    };

    const hasUnsavedChanges = computed(() => {
      if (currentAction.value === "settings") {
        return isSettingsDirty.value;
      }

      if (currentAction.value === "edit_mail_templates") {
        return mailTemplatesDirty.value;
      }

      if (isReminderAction.value) {
        return isReminderDirty.value;
      }

      return false;
    });

    const openSettings = () => {
      Object.assign(settingsForm, settingsInitial.value);
      currentAction.value = "settings";
    };

    const openReminderForm = () => {
      currentReminderToEdit.value = null;
      Object.assign(reminderForm, emptyReminderForm());
      reminderInitial.value = normalizeReminderForm(reminderForm);
      currentAction.value = "reminder_form";
    };

    const openTemplateEditor = () => {
      mailTemplatesDirty.value = false;
      currentAction.value = "edit_mail_templates";
    };

    const handleMenuClick = ({ key }) => {
      if (key === "settings") {
        openSettings();
      }

      if (key === "edit_mail_templates") {
        openTemplateEditor();
      }
    };

    const editReminder = (notice) => {
      currentReminderToEdit.value = notice;
      reminderForm.message = notice.message;
      reminderForm.send_to_email = !notice.mail_sent;

      const dateTime = dayjs(notice.send_at);
      reminderForm.date = dateTime;
      reminderForm.time = dateTime;

      reminderInitial.value = normalizeReminderForm(reminderForm);
      currentAction.value = "edit_reminder";
    };

    const deleteReminder = async (id) => {
      actionLoading.value = true;

      try {
        await store.dispatch("notifications/deleteReminder", { id });
        message.success("Напоминание удалено");
        await fetchLatestNotifications();
      } finally {
        actionLoading.value = false;
      }
    };

    const submitNotificationOptions = async () => {
      actionLoading.value = true;

      try {
        await store.dispatch("notifications/saveOptionsNotification", settingsForm);
        settingsInitial.value = { ...settingsForm };
        message.success("Данные сохранены");
      } finally {
        actionLoading.value = false;
      }
    };

    const submitNoticeForm = async () => {
      if (!reminderForm.message || !reminderForm.date || !reminderForm.time) {
        message.info("Заполните все поля");
        return;
      }

      const sendAt = dayjs(reminderForm.date)
          .hour(dayjs(reminderForm.time).hour())
          .minute(dayjs(reminderForm.time).minute())
          .second(0)
          .format("YYYY-MM-DD HH:mm:ss");

      const payload = {
        send_at: sendAt,
        message: reminderForm.message,
        send_email: reminderForm.send_to_email,
      };

      actionLoading.value = true;

      try {
        if (currentReminderToEdit.value) {
          await store.dispatch("notifications/editNotification", {
            id: currentReminderToEdit.value.id,
            ...payload,
          });
        } else {
          await store.dispatch("notifications/createNotification", payload);
        }

        reminderInitial.value = normalizeReminderForm(reminderForm);
        message.success("Данные сохранены");
        clearCurrentAction();
        await fetchLatestNotifications();
      } finally {
        actionLoading.value = false;
      }
    };

    const saveEditedTemplates = async () => {
      actionLoading.value = true;

      try {
        await templateEditor.value?.saveTemplates?.();
        mailTemplatesDirty.value = false;
        message.success("Данные сохранены");
      } finally {
        actionLoading.value = false;
      }
    };

    const handleAction = async () => {
      if (!currentAction.value) {
        return;
      }

      if (currentAction.value === "settings") {
        await submitNotificationOptions();
        return;
      }

      if (isReminderAction.value) {
        await submitNoticeForm();
        return;
      }

      if (currentAction.value === "edit_mail_templates") {
        await saveEditedTemplates();
      }
    };

    const clearCurrentAction = () => {
      if (currentAction.value === "settings") {
        Object.assign(settingsForm, settingsInitial.value);
      }

      if (isReminderAction.value) {
        Object.assign(reminderForm, emptyReminderForm());
        reminderInitial.value = normalizeReminderForm(reminderForm);
        currentReminderToEdit.value = null;
      }

      if (currentAction.value === "edit_mail_templates") {
        templateEditor.value?.reset?.();
        mailTemplatesDirty.value = false;
      }

      currentAction.value = null;
    };

    const onTemplateEditorDirtyChange = (value) => {
      mailTemplatesDirty.value = !!value;
    };

    const showFeedbackForm = () => {
      store.commit("support/showForm");
    };

    return {
      latestNotifications,
      isPreloader,
      hasNew,
      menuKey,
      currentAction,
      actionLoading,
      isMobile,
      headerTranslate,
      settingsForm,
      reminderForm,
      templateEditor,
      canSave,
      isReminderAction,
      getRight,
      close,
      readNotification,
      readAllNotifications,
      openReminderForm,
      editReminder,
      deleteReminder,
      handleAction,
      clearCurrentAction,
      handleMenuClick,
      onTemplateEditorDirtyChange,
      showFeedbackForm,
      confirmClose,
      cancelCloseConfirm,
      closeConfirmOpen
    };
  },
};
</script>

<style lang="scss">
.notification-icon {
  width: 16px;
  height: 16px;
  cursor: pointer;
  padding: 0;
}

.notifications {
  .popper {
    -webkit-box-shadow: 0px 5px 10px 2px rgba(34, 60, 80, 0.11) !important;
    -moz-box-shadow: 0px 5px 10px 2px rgba(34, 60, 80, 0.11) !important;
    box-shadow: 0px 5px 10px 2px rgba(34, 60, 80, 0.11) !important;
    color: var(--common-text-color) !important;

    &:hover {
      color: var(--common-text-color) !important;
    }

    #arrow::before,
    #arrow:hover::before {
      -webkit-box-shadow: 0px 5px 10px -3px rgba(34, 60, 80, 0.2);
      -moz-box-shadow: 0px 5px 10px -3px rgba(34, 60, 80, 0.2);
      box-shadow: 0px 5px 10px -3px rgba(34, 60, 80, 0.2);
    }
  }
}
</style>

<style lang="scss" scoped>
.notifications {
  &__notification-btn {
    position: relative;
    color: rgba(0, 0, 0, 0.5);
    display: flex;

    &:hover {
      color: rgba(0, 0, 0, 0.75);
    }
  }
}

.notifications__notification-btn.new {
  color: white;
}

.dot {
  display: none;
  position: absolute;
  top: -10px;
  right: -23px;
  height: 20px;
  min-width: 25px;
  background-color: var(--color-error);
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0 4px;
  border: 1px solid #ffffff;
  border-radius: 999px;
  font-size: 14px;
}

.new .dot {
  display: flex;
}

@media (max-width: 768px) {
  .notifications-body {
    width: 300px;
  }
}

.inline-block {
  margin: 0 !important;
  border: 0 !important;
}

.notification-title {
  width: fit-content;
  position: relative;
  display: flex;
  align-items: center;
  column-gap: 8px;

  .dot {
    position: unset;
    color: #ffffff;
  }
}

.close-icon {
  color: var(--grey-text);
  cursor: pointer;
}

.close-icon:hover {
  color: var(--main-font);
}

.drawer-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tabs {
  width: fit-content;

  :deep(.ant-tabs-nav-more) {
    display: none;
  }
}

.spinner {
  margin-top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: $mobile-breakpoint) {
    margin-top: 25%;
  }
}

.date-header {
  font-weight: 400;
  font-size: 14px;
  line-height: 110%;
}

:deep(.ant-tabs-top > .ant-tabs-nav) {
  margin-bottom: 0 !important;
}

.recent {
  font-weight: 600;
}

.notice-card {
  padding: 12px 16px !important;
  box-shadow: unset;

  :deep(.ant-card-body) {
    padding: 0 !important;
    font-weight: 400;
    font-size: 14px;
  }

  .read-button-wrapper {
    @media (max-width: $mobile-breakpoint) {
      width: 100%;
    }
  }

  .read-button {
    @media (max-width: $mobile-breakpoint) {
      width: 100%;
    }
  }

  .notice-card-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    @media (max-width: $mobile-breakpoint) {
      flex-direction: column;
      align-items: flex-start;

      .secondary-button {
        width: 100%;
      }
    }
  }
}

.new-notice {
  background-color: var(--polar-green);
}

:deep(.ant-tabs .ant-tabs-tab + .ant-tabs-tab) {
  margin-left: 16px !important;
}

.notice-card-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-button,
.footer-button-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.mobile-footer {
  flex-direction: column-reverse;

  button {
    width: 100%;
  }
}

.ant-dropdown-trigger.ant-dropdown-open {
  border-color: var(--main-green);
}

.empty-result {
  margin-top: 8px;
}

.sub-header {
  display: flex;
  column-gap: 12px;
  align-items: center;
}

.setting-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.settings-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.setting-block {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.setting-label {
  margin-bottom: 12px;
}

.setting-row {
  display: flex;
  column-gap: 8px;
  align-items: center;
}

.setting-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  margin-bottom: 16px;
}

.setting-card-block {
  :deep(.ant-card-body) {
    padding: 16px !important;
  }
}

.action-button-wrapper {
  cursor: pointer;
}

.notice-form {
  :deep(.ant-form-item) {
    margin-bottom: 12px !important;
  }

  :deep(.ant-form-item-label) {
    padding-bottom: 4px;

    label {
      color: var(--grey-text);
    }
  }
}

.blue-alert {
  border: unset;
  border-radius: 6px;

  :deep(.ant-alert-message) {
    padding: 8px 0;
  }
}

.control-button,
.footer-button .secondary-button.ant-dropdown-trigger {
  padding: 3px 9px;
}

.control-button-wrapper {
  display: flex;
  column-gap: 4px;
}

.footer-button {
  :deep(.ant-btn) {
    margin-inline-start: unset !important;
  }
}

.feedback-action-text {
  text-decoration: underline;
  cursor: pointer;
}

:deep(.reset-template-popconfirm .ant-popover-message) {
  margin-bottom: 8px;
}

.pc-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.pc-warn-ic {
  color: var(--sunset-orange);
  font-size: 16px;
  margin-top: 2px;
}

.pc-title {
  font-weight: 600;
  line-height: 20px;
}

.pc-desc {
  max-width: 260px;
  color: #595959;
  line-height: 20px;
}
</style>
